import { useState, useEffect, useCallback } from 'react';
import { productService } from '../services/productService';
import type { Product, ProductFilters } from '../types/product';
import { ApiError } from '../utils/errorHandling';

interface UseProductsReturn {
  products: Product[];
  filteredProducts: Product[];
  loading: boolean;
  error: string | null;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  filters: ProductFilters;
  setFilters: (filters: ProductFilters) => void;
  viewAllOrgs: boolean;
  setViewAllOrgs: (enabled: boolean) => void;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  totalProducts: number;
  activeProducts: number;
  draftProducts: number;
  refreshProducts: () => Promise<void>;
  fetchProducts: (page?: number, filters?: ProductFilters, limit?: number, viewAllOrgs?: boolean) => Promise<void>;
  updateProductOptimistically: (id: string, updatedProduct: Partial<Product>) => void;
  deleteProductOptimistically: (id: string) => void;
}

export function useProducts(): UseProductsReturn {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<ProductFilters>({});
  const [viewAllOrgs, setViewAllOrgs] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0,
  });

  // Calculate filtered products (for client-side filtering if needed)
  const filteredProducts: Product[] = products;

  // Calculate statistics
  const totalProducts = pagination.total;
  const activeProducts = products.filter(p => p.status === 'ACTIVE').length;
  const draftProducts = products.filter(p => p.status === 'DRAFT').length;

  // Fetch products from API
  const fetchProducts = useCallback(async (
    page: number = 1,
    currentFilters: ProductFilters = filters,
    limit: number = pagination.limit,
    allOrgs: boolean = viewAllOrgs
  ) => {
    try {
      setLoading(true);
      setError(null);

      // Build filters object including search query
      const apiFilters: ProductFilters = {
        ...currentFilters,
        ...(searchQuery && { search: searchQuery }),
      };

      const response = await productService.getProducts(page, apiFilters, limit, allOrgs);
      
      setProducts(response.products);
      setPagination(response.pagination);
    } catch (err) {
      console.error('Error fetching products:', err);
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Failed to load products. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [filters, searchQuery, pagination.limit, viewAllOrgs]);

  // Refresh products (reload current page with current filters)
  const refreshProducts = useCallback(async () => {
    await fetchProducts(pagination.page, filters, pagination.limit, viewAllOrgs);
  }, [fetchProducts, pagination.page, filters, pagination.limit, viewAllOrgs]);

  // Optimistic update for product modifications
  const updateProductOptimistically = useCallback((id: string, updatedProduct: Partial<Product>) => {
    setProducts(prevProducts => 
      prevProducts.map(product => 
        product.id === id ? { ...product, ...updatedProduct } : product
      )
    );
  }, []);

  // Optimistic delete (remove from list)
  const deleteProductOptimistically = useCallback((id: string) => {
    setProducts(prevProducts => prevProducts.filter(product => product.id !== id));
    
    // Update pagination totals
    setPagination(prev => ({
      ...prev,
      total: Math.max(0, prev.total - 1),
      totalPages: Math.ceil(Math.max(0, prev.total - 1) / prev.limit),
    }));
  }, []);

  // Initial load
  useEffect(() => {
    fetchProducts();
  }, []); // Only run on mount

  // Re-fetch when filters change
  useEffect(() => {
    if (products.length > 0) { // Don't run on initial mount
      fetchProducts(1, filters, pagination.limit, viewAllOrgs);
    }
  }, [filters, viewAllOrgs]); // Don't include fetchProducts to avoid infinite loops

  return {
    products,
    filteredProducts,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    viewAllOrgs,
    setViewAllOrgs,
    pagination,
    totalProducts,
    activeProducts,
    draftProducts,
    refreshProducts,
    fetchProducts,
    updateProductOptimistically,
    deleteProductOptimistically,
  };
} 